import { storeToRefs } from 'pinia'
import errorParser from '@/utils/errorParser'
import { logService, debugService, envService } from '@nsftx/seven-client-retail-utils'
import { getCurrencySymbol } from '@nsftx/seven-nxg-retail-vue-sdk'
import { getDeviceAccessToken, useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  useIntegrator,
  type IntegratorParentPOSConfig,
} from '@nsftx/seven-client-retail-vue-integrator'
import { useGamesStore, useTokenPayloadStore } from '@nsftx/seven-client-retail-vue-sgp'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import integratorPosBettingService from './integratorPosBettingService'
import { integratorPOSEvents } from './integratorPosEventsService'
import {
  buildIntegratorChildren,
  resolveLinkCodeFromChildId as resolveLinkCodeFromChildIdUtil,
} from './integratorProductUtils'

const LOG_PREFIX = '[integratorPOSService]'
const UI_SETTINGS = {
  offsetTop: '58px',
  offsetBottom: '58px',
  betslipWidth: '300px',
}

const resolveLinkCodeFromChildId = (childId: string): string | undefined => {
  const { gameLinks } = useGamesStore()
  const { getByGameId } = useGameConfigurationStore()

  return resolveLinkCodeFromChildIdUtil(childId, gameLinks, getByGameId)
}

const initPOSIntegrator = () => {
  initIntegrator()
}

const initIntegrator = () => {
  const gameStores = useGamesStore()
  const dmStore = useDeviceManagementStore()
  const { getByGameId } = useGameConfigurationStore()
  const { gameLinks } = storeToRefs(gameStores)
  const { appData } = storeToRefs(dmStore)
  const accessToken = getDeviceAccessToken()
  const { baseCurrency } = storeToRefs(useTokenPayloadStore())
  const { createIntegrator } = useIntegrator()

  const env = envService.getEnvironment()
  const debug = debugService.isDebugMode() || envService.isDevelopmentEnv()
  const tenantId = appData.value?.betshop.companyUuid || ''
  const children = buildIntegratorChildren(gameLinks.value, getByGameId)
  const token = accessToken.value || ''
  const activeCurrency = baseCurrency.value || ''
  const activeCurrencySymbol = getCurrencySymbol(activeCurrency)

  try {
    const config: IntegratorParentPOSConfig = {
      app: {
        id: 'retail',
        version: '1.0.0',
        displayId: 'RETAIL',
      },
      context: {
        tenantId,
        env,
        debug,
      },
      children,
      auth: {
        token,
      },
      settings: {
        activeCurrency: {
          code: activeCurrency,
          symbol: activeCurrencySymbol,
        },
        activeLocale: 'en',
        oddsOption: 'DECIMAL',
        platformVal: undefined,
        ui: UI_SETTINGS,
      },
      env,
      events: integratorPOSEvents,
    }

    createIntegrator(config, accessToken)

    logService.debug(`${LOG_PREFIX} Integrator successfully initialized.`, {
      code: 'SS_INTEGRATOR_INIT_SUCCESS',
      details: {
        children,
      },
    })

    integratorPosBettingService.init()
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to initialize integrator.`, {
      code: 'SS_INTEGRATOR_INIT_ERROR',
      ...errorParser.parseUpstream(error),
    })
  }
}

export { initPOSIntegrator, resolveLinkCodeFromChildId }
